import React, { useState, useEffect, useRef, useCallback } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { Camera, RefreshCw, Upload, Sparkles } from "lucide-react";

const defaultSettings = {
  "canvasSize": {
    "width": 919,
    "height": 456
  },
  "handles": {
    "image1": [
      { "x": 553, "y": 34 },
      { "x": 710, "y": 73 },
      { "x": 713, "y": 301 },
      { "x": 557, "y": 304 }
    ],
    "image2": [
      { "x": 290, "y": 103 },
      { "x": 540, "y": 35 },
      { "x": 542, "y": 304 },
      { "x": 290, "y": 304 }
    ]
  }
};

const TruckCustomizer: React.FC = () => {
  const [sideLogoFile, setSideLogoFile] = useState<File | null>(null);
  const [backLogoFile, setBackLogoFile] = useState<File | null>(null);
  const [sideLogoUrl, setSideLogoUrl] = useState<string>("");
  const [backLogoUrl, setBackLogoUrl] = useState<string>("");
  const [truckImage, setTruckImage] = useState<HTMLImageElement | null>(null);
  const [transparentTruckImage, setTransparentTruckImage] = useState<HTMLImageElement | null>(null);
  const [showTruckBackground, setShowTruckBackground] = useState<boolean>(true);
  const [imageFilter, setImageFilter] = useState<number>(100);
  const fixedHandles = {
    image1: defaultSettings.handles.image1,
    image2: defaultSettings.handles.image2
  };
  const [dragActive, setDragActive] = useState<"side" | "back" | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [cachedSideLogoImage, setCachedSideLogoImage] = useState<HTMLImageElement | null>(null);
  const [cachedBackLogoImage, setCachedBackLogoImage] = useState<HTMLImageElement | null>(null);

  // Initialize on component mount
  useEffect(() => {
    if (canvasRef.current && canvasContainerRef.current) {
      initializeCanvas();
      preloadImages();
    }
  }, []);

  // Initialize canvas with default settings
  const initializeCanvas = () => {
    if (canvasRef.current && canvasContainerRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (ctx) {
        // Set initial canvas size
        const containerWidth = canvasContainerRef.current.clientWidth;
        const aspectRatio = defaultSettings.canvasSize.width / defaultSettings.canvasSize.height;
        const newWidth = Math.min(containerWidth, defaultSettings.canvasSize.width);
        const newHeight = newWidth / aspectRatio;

        canvas.width = newWidth;
        canvas.height = newHeight;
      }
    }
  };

  // Preload images
  const preloadImages = () => {
    // Load truck background image (with background)
    const truckImg = new Image();
    truckImg.onload = () => {
      setTruckImage(truckImg);
    };
    truckImg.src = "/images/image-gen/Truck_Mock.jpg";

    // Load transparent truck image (without background)
    const transparentTruckImg = new Image();
    transparentTruckImg.onload = () => {
      setTransparentTruckImage(transparentTruckImg);
    };
    transparentTruckImg.src = "/images/image-gen/Media_Truck.png";

    // Load back sign
    fetch("/images/image-gen/MT_Truck_Back.png")
      .then(response => response.blob())
      .then(blob => {
        const file = new File([blob], "MT_Truck_Back.png", { type: "image/png" });
        setBackLogoFile(file);
        const url = URL.createObjectURL(file);
        setBackLogoUrl(url);

        const backImg = new Image();
        backImg.onload = () => {
          setCachedBackLogoImage(backImg);
        };
        backImg.src = url;
      });

    // Load side sign
    fetch("/images/image-gen/MT_Truck_Side.png")
      .then(response => response.blob())
      .then(blob => {
        const file = new File([blob], "MT_Truck_Side.png", { type: "image/png" });
        setSideLogoFile(file);
        const url = URL.createObjectURL(file);
        setSideLogoUrl(url);

        const sideImg = new Image();
        sideImg.onload = () => {
          setCachedSideLogoImage(sideImg);
        };
        sideImg.src = url;
      });
  };

  // Compute projective transform matrix (3x3)
  const computeProjectiveTransform = useCallback((src: { x: number, y: number }[], dst: { x: number, y: number }[]) => {
    // Create the coefficient matrix for the system of equations
    const A: number[][] = [];
    const b: number[] = [];

    for (let i = 0; i < 4; i++) {
      const srcPoint = src[i];
      const dstPoint = dst[i];
      if (srcPoint && dstPoint) {
        // For x coordinate
        A.push([
          srcPoint.x, srcPoint.y, 1, 0, 0, 0, -srcPoint.x * dstPoint.x, -srcPoint.y * dstPoint.x
        ]);
        b.push(dstPoint.x);

        // For y coordinate
        A.push([
          0, 0, 0, srcPoint.x, srcPoint.y, 1, -srcPoint.x * dstPoint.y, -srcPoint.y * dstPoint.y
        ]);
        b.push(dstPoint.y);
      }
    }

    // Solve the system of equations using Gaussian elimination
    const h = solveLinearSystem(A, b);

    // Return the 3x3 transformation matrix
    return [
      [h[0], h[1], h[2]],
      [h[3], h[4], h[5]],
      [h[6], h[7], 1]
    ];
  }, []);

  // Check if a point is inside a quadrilateral
  const isPointInQuadrilateral = useCallback((x: number, y: number, quad: { x: number, y: number }[]) => {
    // Using the winding number algorithm to determine if point is inside the quad
    let wn = 0; // Winding number

    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4;

      const quadI = quad[i];
      const quadJ = quad[j];
      if (quadI && quadJ) {
        if (quadI.y <= y) {
          if (quadJ.y > y && isLeft(quadI, quadJ, { x, y }) > 0) {
            wn++;
          }
        } else {
          if (quadJ.y <= y && isLeft(quadI, quadJ, { x, y }) < 0) {
            wn--;
          }
        }
      }
    }

    return wn !== 0;
  }, []);

  // Draw transformed image using perspective transform
  const drawTransformedImageToCanvas = useCallback((
    ctx: CanvasRenderingContext2D,
    image: HTMLImageElement,
    handles: { x: number, y: number }[]
  ) => {
    if (!image || handles.length !== 4) return;

    // Source points (original image rectangle)
    const srcPoints = [
      { x: 0, y: 0 },                          // Top-left
      { x: image.width, y: 0 },                // Top-right
      { x: image.width, y: image.height },     // Bottom-right
      { x: 0, y: image.height }                // Bottom-left
    ];

    // Destination points (where to draw on canvas)
    const dstPoints = handles;

    // Compute inverse transformation matrix
    const inverseMatrix = computeProjectiveTransform(dstPoints, srcPoints);

    // Create an offscreen canvas for the original image
    const offCanvas = document.createElement('canvas');
    offCanvas.width = image.width;
    offCanvas.height = image.height;
    const offCtx = offCanvas.getContext('2d', { willReadFrequently: true });
    if (!offCtx) return;
    offCtx.drawImage(image, 0, 0);

    // Get image data
    const imageData = offCtx.getImageData(0, 0, offCanvas.width, offCanvas.height);
    const data = imageData.data;

    // Find bounding box of the quadrilateral to minimize pixel processing
    const minX = dstPoints[0] && dstPoints[1] && dstPoints[2] && dstPoints[3] ? Math.max(0, Math.min(dstPoints[0].x, dstPoints[1].x, dstPoints[2].x, dstPoints[3].x)) : 0;
    const maxX = dstPoints[0] && dstPoints[1] && dstPoints[2] && dstPoints[3] ? Math.min(canvasRef.current?.width || 0, Math.max(dstPoints[0].x, dstPoints[1].x, dstPoints[2].x, dstPoints[3].x)) : 0;
    const minY = dstPoints[0] && dstPoints[1] && dstPoints[2] && dstPoints[3] ? Math.max(0, Math.min(dstPoints[0].y, dstPoints[1].y, dstPoints[2].y, dstPoints[3].y)) : 0;
    const maxY = dstPoints[0] && dstPoints[1] && dstPoints[2] && dstPoints[3] ? Math.min(canvasRef.current?.height || 0, Math.max(dstPoints[0].y, dstPoints[1].y, dstPoints[2].y, dstPoints[3].y)) : 0;

    // Create a new canvas for this warped image
    const warpedCanvas = document.createElement('canvas');
    warpedCanvas.width = canvasRef.current?.width || 0;
    warpedCanvas.height = canvasRef.current?.height || 0;
    const warpedCtx = warpedCanvas.getContext('2d', { willReadFrequently: true });
    if (!warpedCtx) return;

    // Create ImageData for the warped image
    const warpedImageData = warpedCtx.createImageData(warpedCanvas.width, warpedCanvas.height);
    const warpedData = warpedImageData.data;

    // Loop through the bounding box of the quadrilateral
    for (let y = Math.floor(minY); y <= Math.ceil(maxY); y++) {
      for (let x = Math.floor(minX); x <= Math.ceil(maxX); x++) {
        // Check if this pixel is inside the quadrilateral
        if (isPointInQuadrilateral(x, y, dstPoints)) {
          // Apply inverse mapping to find the corresponding source pixel
          const invPoint = applyTransform(inverseMatrix, x, y);

          if (invPoint.x >= 0 && invPoint.x < image.width &&
            invPoint.y >= 0 && invPoint.y < image.height) {

            // Bilinear interpolation
            const x1 = Math.floor(invPoint.x);
            const y1 = Math.floor(invPoint.y);
            const x2 = Math.min(x1 + 1, image.width - 1);
            const y2 = Math.min(y1 + 1, image.height - 1);

            const dx = invPoint.x - x1;
            const dy = invPoint.y - y1;

            // Get the four surrounding pixels
            const idx1 = (y1 * image.width + x1) * 4;
            const idx2 = (y1 * image.width + x2) * 4;
            const idx3 = (y2 * image.width + x1) * 4;
            const idx4 = (y2 * image.width + x2) * 4;

            // Calculate the destination pixel index
            const destIdx = (y * warpedCanvas.width + x) * 4;

            // Interpolate each color channel
            for (let c = 0; c < 3; c++) { // Only RGB channels, not alpha
              const data1 = data[idx1 + c];
              const data2 = data[idx2 + c];
              const data3 = data[idx3 + c];
              const data4 = data[idx4 + c];
              if (data1 && data2 && data3 && data4) {
                const top = data1 * (1 - dx) + data2 * dx;
                const bottom = data3 * (1 - dx) + data4 * dx;
                warpedData[destIdx + c] = Math.round(top * (1 - dy) + bottom * dy);
              }
            }

            // Set alpha to full opacity if the source pixel has alpha
            const data1 = data[idx1 + 3];
            const data2 = data[idx2 + 3];
            const data3 = data[idx3 + 3];
            const data4 = data[idx4 + 3];
            if (data1 && data2 && data3 && data4) {
              const topAlpha = data1 * (1 - dx) + data2 * dx;
              const bottomAlpha = data3 * (1 - dx) + data4 * dx;
              const interpolatedAlpha = Math.round(topAlpha * (1 - dy) + bottomAlpha * dy);

              // Only consider a pixel if its alpha is significant
              if (interpolatedAlpha > 10) {
                warpedData[destIdx + 3] = 255; // Full opacity for visible pixels
              } else {
                warpedData[destIdx + 3] = 0; // Fully transparent for invisible pixels
              }
            }
          }
        }
      }
    }

    // Put the warped image data on the offscreen canvas
    warpedCtx.putImageData(warpedImageData, 0, 0);

    // Draw the warped image onto the destination canvas
    ctx.drawImage(warpedCanvas, 0, 0);
  }, [computeProjectiveTransform, isPointInQuadrilateral]);

  // Draw on canvas - keep it simple
  const draw = useCallback(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background
    if (showTruckBackground && truckImage) {
      ctx.drawImage(truckImage, 0, 0, canvas.width, canvas.height);
    } else if (!showTruckBackground && transparentTruckImage) {
      ctx.drawImage(transparentTruckImage, 0, 0, canvas.width, canvas.height);
    }

    // Get scale factors for responsive transformations
    const scaleX = canvas.width / defaultSettings.canvasSize.width;
    const scaleY = canvas.height / defaultSettings.canvasSize.height;

    // Create a temporary canvas for applying the brightness filter
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
    if (!tempCtx) return;

    // Scaled handle positions for back sign
    const scaledBackHandles = fixedHandles.image1.map(handle => ({
      x: handle.x * scaleX,
      y: handle.y * scaleY
    }));

    // Scaled handle positions for side sign
    const scaledSideHandles = fixedHandles.image2.map(handle => ({
      x: handle.x * scaleX,
      y: handle.y * scaleY
    }));

    // Draw back sign if exists and cached
    if (backLogoUrl && cachedBackLogoImage) {
      // Clear temp canvas before drawing
      tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

      // Draw to temp canvas
      drawTransformedImageToCanvas(tempCtx, cachedBackLogoImage, scaledBackHandles);

      // Apply brightness filter
      const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      applyBrightnessFilter(imageData, imageFilter);
      tempCtx.putImageData(imageData, 0, 0);

      // Draw from temp canvas to main canvas
      ctx.drawImage(tempCanvas, 0, 0);
    }

    // Draw side sign if exists and cached
    if (sideLogoUrl && cachedSideLogoImage) {
      // Clear temp canvas before drawing
      tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

      // Draw to temp canvas
      drawTransformedImageToCanvas(tempCtx, cachedSideLogoImage, scaledSideHandles);

      // Apply brightness filter
      const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      applyBrightnessFilter(imageData, imageFilter);
      tempCtx.putImageData(imageData, 0, 0);

      // Draw from temp canvas to main canvas
      ctx.drawImage(tempCanvas, 0, 0);
    }
  }, [backLogoUrl, cachedBackLogoImage, cachedSideLogoImage, drawTransformedImageToCanvas, fixedHandles.image1, fixedHandles.image2, imageFilter, showTruckBackground, sideLogoUrl, transparentTruckImage, truckImage]);

  // Handle drag events
  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>, type: "side" | "back") => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(type);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>, type: "side" | "back") => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];

      if (type === "side") {
        setSideLogoFile(file);
        const url = URL.createObjectURL(file);
        setSideLogoUrl(url);

        // Load the image with proper handling
        const img = new Image();
        img.onload = () => {
          setCachedSideLogoImage(img);
          draw(); // Redraw when loaded
        };
        img.src = url;

        toast({
          title: "Side Logo Uploaded",
          description: "Your logo has been added to the preview.",
        });
      } else {
        setBackLogoFile(file);
        const url = URL.createObjectURL(file);
        setBackLogoUrl(url);

        // Load the image with proper handling
        const img = new Image();
        img.onload = () => {
          setCachedBackLogoImage(img);
          draw(); // Redraw when loaded
        };
        img.src = url;

        toast({
          title: "Back Logo Uploaded",
          description: "Your logo has been added to the preview."
        });
      }
    }
  };

  // Reset to default state
  const resetLogos = () => {
    // Reload the default images
    fetch("/images/image-gen/MT_Truck_Side.png")
      .then(response => response.blob())
      .then(blob => {
        const file = new File([blob], "MT_Truck_Side.png", { type: "image/png" });
        setSideLogoFile(file);
        const url = URL.createObjectURL(file);
        setSideLogoUrl(url);

        // Cache the image with proper loading
        const img = new Image();
        img.onload = () => {
          setCachedSideLogoImage(img);
          draw(); // Redraw when loaded
        };
        img.src = url;
      });

    fetch("/images/image-gen/MT_Truck_Back.png")
      .then(response => response.blob())
      .then(blob => {
        const file = new File([blob], "MT_Truck_Back.png", { type: "image/png" });
        setBackLogoFile(file);
        const url = URL.createObjectURL(file);
        setBackLogoUrl(url);

        // Cache the image with proper loading
        const img = new Image();
        img.onload = () => {
          setCachedBackLogoImage(img);
          draw(); // Redraw when loaded
        };
        img.src = url;
      });

    toast({
      title: "Reset Complete",
      description: "All logos have been reset to default."
    });
  };

  // Toggle background
  const toggleBackground = () => {
    setShowTruckBackground(!showTruckBackground);

    toast({
      title: showTruckBackground ? "Background Removed" : "Background Restored",
      description: showTruckBackground
        ? "Switched to transparent truck view"
        : "Switched to background truck view"
    });
  };

  // Save the resulting image
  const saveImage = () => {
    if (!canvasRef.current) return;

    const link = document.createElement('a');
    link.download = 'truck-with-branding.png';
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();

    toast({
      title: "Image Saved",
      description: "Your truck design has been saved to your downloads."
    });
  };

  // Solve a system of linear equations using Gaussian elimination
  const solveLinearSystem = (A: number[][], b: number[]) => {
    const n = b.length;

    // Augmented matrix
    const augMatrix = A.map((row, i) => [...row, b[i]]);

    // Gaussian elimination with partial pivoting
    if (augMatrix) {
      for (let i = 0; i < n; i++) {
        // Find pivot
        let maxRow = i;
        let maxVal = Math.abs((augMatrix[i] as number[])[i] as number);

        for (let j = i + 1; j < n; j++) {
          const absVal = Math.abs((augMatrix[j] as number[])[i] as number);
          if (absVal > maxVal) {
            maxVal = absVal;
            maxRow = j;
          }
        }

        // Swap rows
        if (maxRow !== i) {
          [augMatrix[i], augMatrix[maxRow]] = [augMatrix[maxRow] as number[], augMatrix[i] as number[]];
        }

        // Eliminate below
        for (let j = i + 1; j < n; j++) {
          const factor = ((augMatrix[j] as number[])[i] as number) / ((augMatrix[i] as number[])[i] as number);
          for (let k = i; k <= n; k++) {
            ((augMatrix[j] as number[])[k] as number) -= factor * ((augMatrix[i] as number[])[k] as number);
          }
        }
      }
    }

    // Back substitution
    const x = new Array(n).fill(0);
    for (let i = n - 1; i >= 0; i--) {
      let sum = 0;
      for (let j = i + 1; j < n; j++) {
        sum += ((augMatrix[i] as number[])[j] as number) * x[j];
      }
      x[i] = ((augMatrix[i] as number[])[n] as number - sum) / ((augMatrix[i] as number[])[i] as number);
    }

    return x;
  };

  // Apply perspective transformation with a matrix
  const applyTransform = (matrix: number[][], x: number, y: number) => {
    // Compute denominator
    const den = ((matrix[2] as number[])[0] as number) * x + ((matrix[2] as number[])[1] as number) * y + ((matrix[2] as number[])[2] as number);

    // Ensure denominator is not zero
    if (Math.abs(den) < 0.0001) {
      return { x: -1, y: -1 }; // Return out-of-bounds coordinates
    }

    // Compute transformed coordinates
    const transX = (((matrix[0] as number[])[0] as number) * x + ((matrix[0] as number[])[1] as number) * y + ((matrix[0] as number[])[2] as number)) / den;
    const transY = (((matrix[1] as number[])[0] as number) * x + ((matrix[1] as number[])[1] as number) * y + ((matrix[1] as number[])[2] as number)) / den;

    return { x: transX, y: transY };
  };

  // Helper function for isPointInQuadrilateral
  const isLeft = (p0: { x: number, y: number }, p1: { x: number, y: number }, p2: { x: number, y: number }) => {
    return ((p1.x - p0.x) * (p2.y - p0.y) - (p2.x - p0.x) * (p1.y - p0.y));
  };

  // Apply brightness filter to image data
  const applyBrightnessFilter = (imageData: ImageData, brightnessValue: number) => {
    const factor = brightnessValue / 100;
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      // Skip transparent pixels
      if (data[i + 3] === 0) continue;

      // Adjust RGB values based on brightness factor
      data[i] = Math.min(255, (data[i] as number) * factor);     // Red
      data[i + 1] = Math.min(255, (data[i + 1] as number) * factor); // Green
      data[i + 2] = Math.min(255, (data[i + 2] as number) * factor); // Blue
    }
  };

  // Update canvas when relevant states change
  useEffect(() => {
    draw();
  }, [draw, truckImage, sideLogoUrl, backLogoUrl, cachedSideLogoImage, cachedBackLogoImage, showTruckBackground, imageFilter]);

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <Card className="overflow-hidden glass border-primary/20">
        <CardContent className="p-6 space-y-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold flex items-center">
              <Sparkles className="mr-2 h-5 w-5 text-primary animate-pulse-slow" />
              Fleet Branding Customizer
            </h3>

            <div className="space-y-6">
              <div>
                <Label htmlFor="side-logo" className="block mb-2 font-semibold text-gradient">Side Logo (1920×1080px)</Label>
                <Label
                  htmlFor="side-logo"
                  className={`flex flex-col items-center justify-center w-full h-32 rounded-lg cursor-pointer transition-all duration-300 ${dragActive === "side" ? 'border-primary border-2 bg-primary/10' : 'border border-dashed border-primary/30 hover:bg-secondary/50'}`}
                  onDragOver={(e) => handleDragOver(e, "side")}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, "side")}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className={`w-8 h-8 mb-2 ${sideLogoUrl ? 'text-primary' : 'text-muted-foreground'} transition-colors duration-300`} />
                    <p className="mb-2 text-sm text-center">
                      <span className="font-semibold">{sideLogoUrl ? 'Change' : 'Drop'}</span> your file here or <span className="text-primary">browse</span>
                    </p>
                    <p className="text-xs text-muted-foreground text-center">PNG, JPG or SVG (1920×1080px recommended)</p>
                  </div>
                  <Input
                    id="side-logo"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        const file = e.target.files[0];
                        setSideLogoFile(file);
                        const url = URL.createObjectURL(file);
                        setSideLogoUrl(url);

                        // Load the image with proper handling
                        const img = new Image();
                        img.onload = () => {
                          setCachedSideLogoImage(img);
                          draw(); // Redraw when loaded
                        };
                        img.src = url;

                        toast({
                          title: "Side Logo Uploaded",
                          description: "Your logo has been added to the preview.",
                        });
                      }
                    }}
                    className="hidden"
                  />
                </Label>
                {sideLogoUrl && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-8 h-8 bg-secondary/50 rounded-md overflow-hidden">
                      <img src={sideLogoUrl} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                    <span className="truncate flex-1">{sideLogoFile?.name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        // Reload the default side logo
                        fetch("/images/image-gen/MT_Truck_Side.png")
                          .then(response => response.blob())
                          .then(blob => {
                            const file = new File([blob], "MT_Truck_Side.png", { type: "image/png" });
                            setSideLogoFile(file);
                            const url = URL.createObjectURL(file);
                            setSideLogoUrl(url);

                            // Load the image properly
                            const img = new Image();
                            img.onload = () => {
                              setCachedSideLogoImage(img);
                              draw(); // Redraw when loaded
                            };
                            img.src = url;

                            toast({
                              title: "Side Logo Reset",
                              description: "The side logo has been reset to default."
                            });
                          });
                      }}
                    >
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="back-logo" className="block mb-2 font-semibold text-gradient">Back Logo (1080×1080px)</Label>
                <Label
                  htmlFor="back-logo"
                  className={`flex flex-col items-center justify-center w-full h-32 rounded-lg cursor-pointer transition-all duration-300 ${dragActive === "back" ? 'border-primary border-2 bg-primary/10' : 'border border-dashed border-primary/30 hover:bg-secondary/50'}`}
                  onDragOver={(e) => handleDragOver(e, "back")}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, "back")}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className={`w-8 h-8 mb-2 ${backLogoUrl ? 'text-primary' : 'text-muted-foreground'} transition-colors duration-300`} />
                    <p className="mb-2 text-sm text-center">
                      <span className="font-semibold">{backLogoUrl ? 'Change' : 'Drop'}</span> your file here or <span className="text-primary">browse</span>
                    </p>
                    <p className="text-xs text-muted-foreground text-center">PNG, JPG or SVG (1080×1080px recommended)</p>
                  </div>
                  <Input
                    id="back-logo"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        const file = e.target.files[0];
                        setBackLogoFile(file);
                        const url = URL.createObjectURL(file);
                        setBackLogoUrl(url);

                        // Load the image with proper handling
                        const img = new Image();
                        img.onload = () => {
                          setCachedBackLogoImage(img);
                          draw(); // Redraw when loaded
                        };
                        img.src = url;

                        toast({
                          title: "Back Logo Uploaded",
                          description: "Your logo has been added to the preview."
                        });
                      }
                    }}
                    className="hidden"
                  />
                </Label>
                {backLogoUrl && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-8 h-8 bg-secondary/50 rounded-md overflow-hidden">
                      <img src={backLogoUrl} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                    <span className="truncate flex-1">{backLogoFile?.name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        // Reload the default back logo
                        fetch("/images/image-gen/MT_Truck_Back.png")
                          .then(response => response.blob())
                          .then(blob => {
                            const file = new File([blob], "MT_Truck_Back.png", { type: "image/png" });
                            setBackLogoFile(file);
                            const url = URL.createObjectURL(file);
                            setBackLogoUrl(url);

                            // Load the image properly
                            const img = new Image();
                            img.onload = () => {
                              setCachedBackLogoImage(img);
                              draw(); // Redraw when loaded
                            };
                            img.src = url;

                            toast({
                              title: "Back Logo Reset",
                              description: "The back logo has been reset to default."
                            });
                          });
                      }}
                    >
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold flex items-center">
                <Sparkles className="mr-2 h-5 w-5 text-primary animate-pulse-slow" />
                Brightness
              </h3>
              <span className="bg-primary/20 px-2 py-1 rounded text-sm font-medium">{imageFilter}%</span>
            </div>
            <Slider
              value={[imageFilter]}
              min={50}
              max={150}
              step={1}
              onValueChange={(value) => setImageFilter(value[0] as number)}
              className="w-full"
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <Button
              variant="outline"
              onClick={resetLogos}
              className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button
              variant="outline"
              onClick={toggleBackground}
              className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5"
            >
              {showTruckBackground ? "Remove Background" : "Show Background"}
            </Button>
            <Button
              onClick={saveImage}
              className="hover-scale btn-primary-dark glow"
            >
              <Camera className="mr-2 h-4 w-4" />
              Save Image
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col space-y-4">
        <div className="relative glass rounded-lg overflow-hidden border-primary/20 min-h-[200px] md:min-h-[350px] lg:min-h-[456px] flex items-center justify-center p-2 md:p-4 group">
          <div className="absolute top-4 right-4 bg-secondary/80 backdrop-blur-xs px-3 py-1.5 rounded-full text-xs font-medium z-20">
            Truck Preview
          </div>

          <div className="absolute inset-0 bg-linear-to-br from-primary/10 to-transparent pointer-events-none opacity-60"></div>

          {/* Canvas Container */}
          <div
            ref={canvasContainerRef}
            className="canvas-container relative w-full h-full flex items-center justify-center overflow-hidden"
            style={{ maxWidth: '100%' }}
          >
            <canvas
              ref={canvasRef}
              id="canvas"
              className="max-w-full h-auto object-contain"
              style={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                maxWidth: '100%'
              }}
            ></canvas>
          </div>
        </div>

        <div className="glass rounded-lg p-4 border border-primary/20 flex items-center gap-4">
          <div className="rounded-full bg-primary/20 p-1.5 shrink-0">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <p className="text-sm">
            <span className="font-semibold">Pro Tip:</span> Upload your company logos to see them on the truck. Adjust brightness or toggle background as needed.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TruckCustomizer;
